import { useEffect, useRef, useState } from "react";
import { Check, Play, Loader2, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// FIX-VOICESELECTOR-AUTH — SEC-1 endureceu /preferences pra exigir
// Authorization Bearer. Sem o header, backend retornava 401 silencioso
// e a UI caia no fallback "nova" pra todos os users, divergindo da
// voz real tocada pelo TTS (que usa JWT corretamente).
const authHeaders = (token) => ({
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

// Sincronizado com backend routes/preferences.py::ALLOWED_VOICES.
// Descrições reaproveitam a intuição que o usuário costuma ter sobre cada timbre.
const VOICES = [
  { id: "nova",    name: "Nova",    desc: "Feminina, suave", featured: true },
  { id: "alloy",   name: "Alloy",   desc: "Neutra, equilibrada" },
  { id: "echo",    name: "Echo",    desc: "Masculina, clara" },
  { id: "fable",   name: "Fable",   desc: "Jovem, dinâmica" },
  { id: "onyx",    name: "Onyx",    desc: "Masculina, grave" },
  { id: "shimmer", name: "Shimmer", desc: "Feminina, animada" },
];

export default function VoiceSelector({ userId }) {
  const { token } = useAuth();
  const [selected, setSelected] = useState("nova");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(null); // voice id que está carregando/tocando
  const audioRef = useRef(null);

  // Carrega preferência atual
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/preferences`, {
          ...authHeaders(token),
          params: { user_id: userId },  // legacy, backend ignora e usa current_user
        });
        if (!cancelled) setSelected(r.data?.voice || "nova");
      } catch (e) {
        console.warn("[VoiceSelector] failed to load preferences", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, token]);

  // Pausa qualquer áudio em curso ao desmontar
  useEffect(() => () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) {}
      audioRef.current = null;
    }
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_) {}
      audioRef.current = null;
    }
    setPreviewing(null);
  };

  const handlePreview = async (voiceId) => {
    stopAudio();
    setPreviewing(voiceId);
    try {
      // Usa o endpoint GET com cache — instantâneo após a 1ª vez (<20ms).
      const url = `${API}/tts/preview?voice=${encodeURIComponent(voiceId)}`;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { if (audioRef.current === audio) { audioRef.current = null; setPreviewing(null); } };
      audio.onerror = () => { audioRef.current = null; setPreviewing(null); toast.error("Falha ao tocar amostra"); };
      await audio.play();
    } catch (e) {
      setPreviewing(null);
      toast.error("Falha ao tocar amostra");
    }
  };

  const handleSelect = async (voiceId) => {
    if (voiceId === selected || saving) return;
    setSaving(true);
    const prev = selected;
    setSelected(voiceId); // otimista
    try {
      await axios.patch(
        `${API}/preferences`,
        { voice: voiceId },
        { ...authHeaders(token), params: { user_id: userId } },
      );
      toast.success(`Voz atualizada: ${VOICES.find(v => v.id === voiceId)?.name}`);
    } catch (e) {
      setSelected(prev);
      toast.error("Não consegui salvar a voz. Tenta de novo?");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="voice-selector">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {VOICES.map((v) => {
          const isSelected = selected === v.id;
          const isPlaying = previewing === v.id;
          return (
            <motion.button
              key={v.id}
              type="button"
              onClick={() => handleSelect(v.id)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading || saving}
              className={cn(
                "text-left p-4 rounded-xl border transition-colors duration-normal ease-smooth",
                "bg-surface-card hover:bg-surface-card-hover",
                isSelected
                  ? "border-brand-cyan shadow-glow-cyan"
                  : "border-subtle hover:border-strong",
                (loading || saving) && "opacity-60 cursor-not-allowed",
              )}
              data-testid={`voice-card-${v.id}`}
            >
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-display text-h3 text-foreground">{v.name}</span>
                  {v.featured && (
                    <span className="inline-flex items-center gap-1 text-micro text-brand-cyan/90 uppercase tracking-wider">
                      <Sparkles className="w-3 h-3" />
                      recomendada
                    </span>
                  )}
                </div>
                {isSelected && (
                  <span className="w-6 h-6 rounded-full bg-brand-cyan/15 text-brand-cyan flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </span>
                )}
              </div>
              <p className="text-caption text-foreground-muted mb-3">{v.desc}</p>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); handlePreview(v.id); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); handlePreview(v.id); } }}
                className={cn(
                  "inline-flex items-center gap-2 text-caption px-3 py-1.5 rounded-md border transition-colors duration-fast ease-smooth",
                  isPlaying
                    ? "bg-brand-cyan/10 border-brand-cyan/30 text-brand-cyan"
                    : "bg-surface-card-hover border-subtle text-foreground-muted hover:text-foreground hover:border-strong",
                )}
                data-testid={`voice-preview-${v.id}`}
              >
                {isPlaying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                <span>{isPlaying ? "Tocando…" : "Ouvir amostra"}</span>
              </div>
            </motion.button>
          );
        })}
      </div>

      <p className="text-caption text-foreground-subtle">
        A voz escolhida é usada em toda resposta falada do Nexo. Você pode trocar quando quiser.
      </p>
    </div>
  );
}
